import { match } from "ts-pattern";
import { z } from "zod";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import { ControlDataTypeKey } from "../../common";
import { ResourceSchema } from "../../resources";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { DefaultControlInstance } from "../instance";
import { swatchToColorString } from "./conversion";
class Definition extends ControlDefinition {
  constructor(config, version) {
    super(config);
    this.version = version;
  }
  static v1DataType = "color::v1";
  static dataSignature = {
    v1: { [ControlDataTypeKey]: this.v1DataType }
  };
  static type = "makeswift::controls::color";
  static get schema() {
    const type = z.literal(this.type);
    const version = z.literal(1).optional();
    const value = ResourceSchema.colorData;
    const resolvedSwatchValue = ResourceSchema.resolvedColorData;
    const schemas = (resolvedValue) => {
      const data = value.merge(
        z.object({
          [ControlDataTypeKey]: z.literal(this.v1DataType).optional()
        })
      );
      const config = z.object({
        label: z.string().optional(),
        labelOrientation: z.union([z.literal("horizontal"), z.literal("vertical")]).optional(),
        defaultValue: resolvedValue,
        hideAlpha: z.boolean().optional()
      });
      const definition = z.object({
        type,
        config,
        version
      });
      return {
        type,
        value,
        resolvedSwatchValue,
        resolvedValue,
        data,
        config,
        version,
        definition
      };
    };
    return {
      version,
      relaxed: schemas(z.string().optional()),
      strict: schemas(z.string())
    };
  }
  static deserialize(data) {
    if (data.type !== Definition.type)
      throw new Error(
        `Color deserialization: expected '${Definition.type}', got '${data.type}'`
      );
    const { config, version } = Definition.schema.relaxed.definition.parse(data);
    return new ColorDefinition(config, version);
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    return Definition.schema.relaxed;
  }
  get dataSchema() {
    return (this.config.defaultValue === void 0 ? Definition.schema.relaxed : Definition.schema.strict).data;
  }
  safeParse(data) {
    return safeParse(this.dataSchema, data);
  }
  fromData(data) {
    const inputSchema = this.dataSchema.optional();
    return match(data).with(Definition.dataSignature.v1, ({ swatchId, alpha }) => ({
      swatchId,
      alpha
    })).otherwise((value) => value);
  }
  toData(value) {
    return match(this.version).with(1, () => ({
      ...Definition.dataSignature.v1,
      ...value
    })).with(void 0, () => value).exhaustive();
  }
  copyData(data, { replacementContext }) {
    if (data == null)
      return data;
    const replaceSwatchId = (swatchId) => replacementContext.swatchIds.get(swatchId) ?? swatchId;
    const inputSchema = this.dataSchema.optional();
    return match(data).with(Definition.dataSignature.v1, (val) => ({
      ...val,
      swatchId: replaceSwatchId(val.swatchId)
    })).otherwise((val) => ({
      ...val,
      swatchId: replaceSwatchId(val.swatchId)
    }));
  }
  resolveSwatch(data, resolver) {
    const value = this.fromData(data);
    const swatchSub = resolver.resolveSwatch(value?.swatchId);
    const stableValue = StableValue({
      name: `${Definition.type}:swatch`,
      read: () => {
        const swatch = swatchSub.readStable();
        return swatch == null ? void 0 : {
          swatch,
          alpha: value?.alpha ?? 1
        };
      },
      deps: [swatchSub]
    });
    return {
      ...stableValue,
      triggerResolve: async (currentValue) => {
        if (currentValue == null) {
          await swatchSub.fetch();
        }
      }
    };
  }
  resolveValue(data, resolver) {
    const value = this.fromData(data);
    const swatch = resolver.resolveSwatch(value?.swatchId);
    const stableValue = StableValue({
      name: Definition.type,
      read: () => swatchToColorString(
        swatch.readStable(),
        value?.alpha ?? 1,
        this.config.defaultValue
      ),
      deps: [swatch]
    });
    return {
      ...stableValue,
      triggerResolve: async () => {
        if (swatch.readStable() == null) {
          await swatch.fetch();
        }
      }
    };
  }
  createInstance(sendMessage) {
    return new DefaultControlInstance(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type,
      version: this.version
    });
  }
}
class ColorDefinition extends Definition {
}
function Color(config) {
  return new ColorDefinition(config ?? {}, 1);
}
export {
  Color,
  ColorDefinition
};
//# sourceMappingURL=color.js.map